/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import { useTooltipInPortal, useTooltip } from "@visx/tooltip";
import { When } from "react-if";
import { useParams } from "react-router-dom";
import { BrickLoader, UniversalWrap } from "~/components";
import {
  getColorsPalette,
  StackBarChart,
  getTableColumns,
  Wrapper,
} from "~/components/Charts/CostAnalysis";
import { tooltipStyles } from "~/components/Charts/CostAnalysis/components";
import {
  generateLinkParameters,
  getEndDate,
  mapStackBarChartColors,
} from "~/components/Charts/CostAnalysis/helpers";
import {
  ChartType,
  Granularity,
  LinkParameters,
  Period,
  LinkQueryParameterName,
} from "~/components/Charts/CostAnalysis/types";
import { useCurrency } from "~/hooks";
import { RouteParams } from "../../types";
import { ApplicationCostTableData } from "../ApplicationCostTableData";
import { useApplicationCategoriesCostChart } from "../hooks";

type Props = {
  appId: string;
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  hasFooter: boolean;
  showCostTable: boolean;
};

export function ApplicationCostPerCategoryView({
  appId,
  startDate,
  endDate,
  granularity,
  period,
  hasFooter,
  showCostTable,
}: Props) {
  const { currency } = useCurrency();
  const { application } = useParams<RouteParams>();

  const {
    applicationTopCategoriesCostChartData,
    costPerCategoryTableData,
    costPerCategoryTotal,
    isLoading,
    showError,
    getSubRows,
    categories,
    otherCategoryIds,
  } = useApplicationCategoriesCostChart({
    appId,
    startDate,
    endDate: getEndDate(endDate),
    period,
    granularity,
  });

  const linkParameters: LinkParameters = useMemo(
    () =>
      generateLinkParameters(
        LinkQueryParameterName.APPLICATION,
        [application],
        otherCategoryIds
      ),
    [application, otherCategoryIds]
  );

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const columns = useMemo(
    () =>
      getTableColumns({
        startDate,
        endDate,
        currency,
        data: costPerCategoryTableData,
        hasFooter,
        period,
        chartType: ChartType.COST_PER_CATEGORY,
        colorsPalette: getColorsPalette(ChartType.COST_PER_CATEGORY),
        linkParameters,
      }),
    [
      startDate,
      endDate,
      currency,
      hasFooter,
      costPerCategoryTableData,
      linkParameters,
    ]
  );

  return (
    <UniversalWrap
      loaderProps={{ loading: isLoading, Component: BrickLoader }}
      errorProps={{ error: showError }}
    >
      <Wrapper onMouseLeave={hideTooltip}>
        <ParentSize>
          {(parent) => (
            <>
              <StackBarChart
                width={parent.width}
                height={280}
                data={applicationTopCategoriesCostChartData}
                granularity={granularity}
                loading={isLoading}
                startDate={startDate}
                endDate={endDate}
                period={period}
                showTooltip={showTooltip}
                hideTooltip={hideTooltip}
                colors={mapStackBarChartColors(categories)}
                keys={categories}
                containerRef={containerRef}
              />
              <When condition={tooltipOpen}>
                <TooltipInPortal
                  // set key to random so it correctly updates with parent bounds
                  key={Date.now()}
                  top={tooltipTop}
                  left={tooltipLeft}
                  style={tooltipStyles}
                >
                  {tooltipData}
                </TooltipInPortal>
              </When>
            </>
          )}
        </ParentSize>
      </Wrapper>
      <ApplicationCostTableData
        period={period}
        startDate={startDate}
        endDate={endDate}
        tableData={costPerCategoryTableData}
        chartType={ChartType.COST_PER_CATEGORY}
        total={costPerCategoryTotal}
        tableError={showError}
        columns={columns}
        isLoading={isLoading}
        showCostTable={showCostTable}
        hasFooter={hasFooter}
        getSubRows={getSubRows}
        linkParameters={linkParameters}
      />
    </UniversalWrap>
  );
}
